import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getAuthNavigationLink } from 'helpers/customNavigationRules';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';

import { Slider } from '@atlas/components';
import { SiteConfigManager, useApplicationState } from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';
import { CasinoPrimaryTile, ChipsItem, Icon, Label } from '@atlas/ui';

import { useIsRtl } from 'hooks/useIsRtl';
import { BASE_PATH } from 'src/common/constants';
import { CDN_TYPE_CMS, imageUrlCDN } from 'src/common/utils';

import { LOBBY_PRIMARY_ACTION_TYPES, LOBBY_PRIMARY_BREAKPOINTS } from './constants';

const LobbyPrimaryBlock = ({ blockTitleKey, blocks, imageUrl }) => {
  const [swiper, setSwiper] = useState(null);
  const prevSlide = () => swiper && swiper.slidePrev();
  const nextSlide = () => swiper && swiper.slideNext();
  const [isShowImg, toggleVisibilityImg] = useState(true);
  const handleError = () => toggleVisibilityImg(false);

  const history = useHistory();

  const finalItems = useMemo(() => {
    if (!blocks || !Array.isArray(blocks)) return [];
    return blocks.map(item => ({
      ...item,
      iconUrl: item.imageUrl ? `${encodeURI(imageUrlCDN(item.imageUrl, CDN_TYPE_CMS))}` : '',
      id: item.id,
      key: crypto.randomUUID(),
      title: item.title,
    }));
  }, [blocks]);

  const { authenticated } = useApplicationState();

  const handleItemClick = item => {
    const { actionType, gameId, url } = item;

    if (!authenticated) {
      const requiresAuth = [
        LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_STORE,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MINI_GAMES,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MISSIONS,
      ].includes(actionType);

      if (requiresAuth) {
        if (actionType === LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT) {
          const tournamentsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.TOURNAMENTS);
          history.push(getAuthNavigationLink(history.location.pathname, tournamentsPath));
        } else {
          history.push(`${history.location.pathname}?cmd=signin`);
        }
        return;
      }
    }

    switch (actionType) {
      case LOBBY_PRIMARY_ACTION_TYPES.URL:
      case LOBBY_PRIMARY_ACTION_TYPES.CUSTOM_URL: {
        if (!url) return;
        const isExternal = url.startsWith('http://') || url.startsWith('https://');
        if (isExternal) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          history.push(url);
        }
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.REFER_A_FRIEND:
        break;
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MINI_GAMES:
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MISSIONS:
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_STORE:
        break;
      case LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT: {
        const tournamentsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.TOURNAMENTS);
        history.push(tournamentsPath);
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.PROMOTION: {
        const promotionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);
        history.push(promotionsPath);
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.ACTIVE_GAME:
        if (gameId) {
          const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
          const { game: casinoGamePath } = casinoRoutes;
          history.push(`${casinoPath}/${casinoGamePath}/${gameId}`);
        }
        break;
      default:
        break;
    }
  };

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  if (!finalItems || finalItems.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-primary">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={32}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/primary.png`
                }
                width={32}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
        <div className="casino-lobby__right">
          <ChipsItem
            className="casino-lobby__arrow casino-lobby__arrow-left"
            label={<Icon name="arrow-left" />}
            onClick={prevSlide}
          />
          <ChipsItem
            className="casino-lobby__arrow casino-lobby__arrow-right"
            label={<Icon name="arrow-right" />}
            onClick={nextSlide}
          />
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-primary__slider">
          <Slider
            breakpoints={LOBBY_PRIMARY_BREAKPOINTS}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            onBeforeInit={setSwiper}
            slidesPerView={8}
            spaceBetween={8}
          >
            {finalItems.map(item => (
              <Slider.Slide key={item.key}>
                <CasinoPrimaryTile
                  isLoading={false}
                  isMobile={false}
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default LobbyPrimaryBlock;
